import { http, download, getBlob } from './request'
import { isBusinessSuccessCode } from './types'

export interface VoiceCallRecord {
  id: string
  callId: string
  startTime: string
  answerTime: string
  endTime: string
  caller: string
  callee: string
  customerNumber: string
  areaCode: string
  areaName: string
  outCodeAreaCode: string
  outCodeAreaName: string
  userName: string
  seatId: string
  agentName: string
  agentId: string
  durationSeconds?: number | null
  durationText: string
  ringSeconds?: number | null
  direction: string
  directionLabel: string
  service: string
  detail: string
  mediaType: string
  answerFlag: string
  answerFlagLabel: string
  callRescode: string
  satisfaction: string
  serviceRecord: string
  vcsLastAgentId: string
  vcsLastIvrFlowName: string
  hasRecord: boolean
  mediaNodeId?: string | null
}

export interface VoiceCallPageParams {
  current: number
  size: number
  callId?: string
  callStart?: string
  callEnd?: string
  callerPhone?: string
  calleePhone?: string
  phone?: string
  direction?: string
}

export interface VoiceCallPageResult {
  records: VoiceCallRecord[]
  total: number
  current: number
  size: number
}

export interface VoiceCallOverview {
  todayCallCount: number
  todayInboundCount: number
  todayOutboundCount: number
  todayAvgDurationText: string
}

function num(v: unknown, def = 0): number {
  if (typeof v === 'number' && !Number.isNaN(v)) return v
  if (typeof v === 'string' && v.trim() !== '') {
    const n = Number(v)
    if (!Number.isNaN(n)) return n
  }
  return def
}

function mapRecord(raw: Record<string, unknown> | null | undefined): VoiceCallRecord {
  const o = raw ?? {}
  return {
    id: String(o.id ?? ''),
    callId: String(o.callId ?? ''),
    startTime: String(o.startTime ?? ''),
    answerTime: String(o.answerTime ?? ''),
    endTime: String(o.endTime ?? ''),
    caller: String(o.caller ?? ''),
    callee: String(o.callee ?? ''),
    customerNumber: String(o.customerNumber ?? ''),
    areaCode: String(o.areaCode ?? ''),
    areaName: String(o.areaName ?? ''),
    outCodeAreaCode: String(o.outCodeAreaCode ?? ''),
    outCodeAreaName: String(o.outCodeAreaName ?? ''),
    userName: String(o.userName ?? ''),
    seatId: String(o.seatId ?? ''),
    agentName: String(o.agentName ?? ''),
    agentId: String(o.agentId ?? ''),
    durationSeconds: o.durationSeconds == null ? null : num(o.durationSeconds),
    durationText: String(o.durationText ?? ''),
    ringSeconds: o.ringSeconds == null ? null : num(o.ringSeconds),
    direction: String(o.direction ?? ''),
    directionLabel: String(o.directionLabel ?? ''),
    service: String(o.service ?? ''),
    detail: String(o.detail ?? ''),
    mediaType: String(o.mediaType ?? ''),
    answerFlag: String(o.answerFlag ?? ''),
    answerFlagLabel: String(o.answerFlagLabel ?? ''),
    callRescode: String(o.callRescode ?? ''),
    satisfaction: String(o.satisfaction ?? ''),
    serviceRecord: String(o.serviceRecord ?? ''),
    vcsLastAgentId: String(o.vcsLastAgentId ?? ''),
    vcsLastIvrFlowName: String(o.vcsLastIvrFlowName ?? ''),
    hasRecord: o.hasRecord === true,
    mediaNodeId: o.mediaNodeId ? String(o.mediaNodeId) : null,
  }
}

const BASE = '/biz/overseas/voice'

export async function fetchVoiceCallOverview(): Promise<VoiceCallOverview> {
  const raw = await http.get<Record<string, unknown>>(`${BASE}/stats`)
  const o = raw ?? {}
  return {
    todayCallCount: num(o.todayCallCount),
    todayInboundCount: num(o.todayInboundCount),
    todayOutboundCount: num(o.todayOutboundCount),
    todayAvgDurationText: String(o.todayAvgDurationText || '-'),
  }
}

export async function fetchVoiceCallPage(
  params: VoiceCallPageParams,
): Promise<VoiceCallPageResult> {
  const raw = await http.get<{
    records?: Record<string, unknown>[]
    total?: number
    current?: number
    size?: number
  }>(`${BASE}/records`, { params })
  const records = (raw?.records ?? []).map((row) => mapRecord(row))
  return {
    records,
    total: num(raw?.total),
    current: num(raw?.current, params.current),
    size: num(raw?.size, params.size),
  }
}

export interface VoiceCallTraceNode {
  id: string
  sid: string
  callId: string
  startTime: string
  type: string
  service: string
  detail: string
  ringSeconds?: number | null
  durationSeconds?: number | null
  durationText: string
  agentName: string
  agentId: string
  callRescode: string
  media: string
  answerFlag: string
  answerFlagLabel: string
}

function mapTrace(raw: Record<string, unknown> | null | undefined): VoiceCallTraceNode {
  const o = raw ?? {}
  return {
    id: String(o.id ?? ''),
    sid: String(o.sid ?? ''),
    callId: String(o.callId ?? ''),
    startTime: String(o.startTime ?? ''),
    type: String(o.type ?? ''),
    service: String(o.service ?? ''),
    detail: String(o.detail ?? ''),
    ringSeconds: o.ringSeconds == null ? null : num(o.ringSeconds),
    durationSeconds: o.durationSeconds == null ? null : num(o.durationSeconds),
    durationText: String(o.durationText ?? ''),
    agentName: String(o.agentName ?? ''),
    agentId: String(o.agentId ?? ''),
    callRescode: String(o.callRescode ?? ''),
    media: String(o.media ?? ''),
    answerFlag: String(o.answerFlag ?? ''),
    answerFlagLabel: String(o.answerFlagLabel ?? ''),
  }
}

export async function fetchVoiceCallTrace(callId: string): Promise<VoiceCallTraceNode[]> {
  const raw = await http.get<unknown>(`${BASE}/trace/${encodeURIComponent(callId)}`)
  const list = Array.isArray(raw) ? raw : []
  return list.map((row) => mapTrace(row as Record<string, unknown>))
}

export interface VoiceCallAsrDetail {
  markId: string
  sentence: string
  startMs?: number | null
  endMs?: number | null
}

export interface VoiceCallAsr {
  message: string
  sid: string
  details: VoiceCallAsrDetail[]
}

export async function fetchVoiceCallAsr(callId: string, sid?: string): Promise<VoiceCallAsr> {
  const raw = await http.get<Record<string, unknown>>(`${BASE}/asr/${encodeURIComponent(callId)}`, {
    params: sid ? { sid } : undefined,
  })
  const o = raw ?? {}
  const detailsRaw = Array.isArray(o.details) ? o.details : []
  return {
    message: String(o.message ?? ''),
    sid: String(o.sid ?? ''),
    details: detailsRaw.map((row) => {
      const d = (row ?? {}) as Record<string, unknown>
      return {
        markId: String(d.markId ?? ''),
        sentence: String(d.sentence ?? ''),
        startMs: d.startMs == null ? null : num(d.startMs),
        endMs: d.endMs == null ? null : num(d.endMs),
      }
    }),
  }
}

export type VoiceCallProtocol = Record<string, string>

export async function fetchVoiceCallProtocol(
  callId: string,
  startTime: string,
  endTime: string,
  sid?: string,
): Promise<VoiceCallProtocol[]> {
  const raw = await http.get<unknown>(`${BASE}/protocol/${encodeURIComponent(callId)}`, {
    params: { startTime, endTime, ...(sid ? { sid } : {}) },
  })
  const list = Array.isArray(raw) ? raw : []
  return list.map((row) => {
    const o = (row ?? {}) as Record<string, unknown>
    const mapped: VoiceCallProtocol = {}
    Object.keys(o).forEach((k) => {
      mapped[k] = o[k] == null ? '' : String(o[k])
    })
    return mapped
  })
}

function mediaUrl(callId: string, nodeId?: string | null, mediaType?: string | null): string {
  const q = new URLSearchParams({ callId })
  if (nodeId) q.set('nodeId', nodeId)
  const media = (mediaType || '').trim().toUpperCase()
  if (media === 'AUDIO' || media === 'VIDEO') q.set('mediaType', media)
  return `${BASE}/media?${q.toString()}`
}

export async function downloadVoiceCallRecordingsZip(callId: string): Promise<void> {
  await download(
    `${BASE}/media/zip?callId=${encodeURIComponent(callId)}`,
    `通话录音-${callId}.zip`,
    { timeout: 180000 },
  )
}

export async function fetchVoiceCallRecordAudioBlob(
  callId: string,
  nodeId?: string | null,
  mediaType?: string | null,
): Promise<Blob> {
  try {
    const blob = await getBlob(mediaUrl(callId, nodeId, mediaType), {
      timeout: 120000,
      skipErrorMessage: true,
    })
    const err = await readMediaBizError(blob)
    if (err) throw new Error(err)
    return blob
  } catch (e) {
    const data = (e as { response?: { data?: unknown } })?.response?.data
    if (data instanceof Blob) {
      const err = await readMediaBizError(data)
      if (err) throw new Error(err)
    }
    if (e instanceof Error && e.message.trim() && !isTransportErrorMessage(e.message)) throw e
    throw new Error('录音加载失败')
  }
}

function isTransportErrorMessage(message: string) {
  const text = message.trim()
  return (
    text === 'Network Error' ||
    text.startsWith('timeout of ') ||
    text.startsWith('Request failed with status code ')
  )
}

/** 业务 JSON 或空文件不是可播放录音。超过 8KB 的二进制按录音处理。 */
async function readMediaBizError(blob: Blob): Promise<string | null> {
  if (!(blob instanceof Blob) || blob.size === 0) return '录音加载失败'
  if (blob.size > 8192) return null
  const type = (blob.type || '').toLowerCase()
  if (type.includes('audio') || type.includes('video')) return null
  try {
    const text = (await blob.slice(0, 8192).text()).trim()
    if (!text.startsWith('{')) {
      return type.includes('json') || type.includes('html') ? '录音下载失败' : null
    }
    const parsed = JSON.parse(text) as { msg?: string; message?: string; code?: number }
    if (typeof parsed.code === 'number' && isBusinessSuccessCode(parsed.code)) {
      return '录音加载失败'
    }
    const message = String(parsed.msg || parsed.message || '').trim()
    return message || '录音下载失败'
  } catch {
    return type.includes('json') ? '录音下载失败' : null
  }
}
